import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { hrmApi, adminApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

export function EmployeesPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ['employees'], queryFn: hrmApi.listEmployees });
  const deptQuery = useQuery({ queryKey: ['departments'], queryFn: hrmApi.listDepartments });

  const createMut = useMutation({
    mutationFn: (p: any) => hrmApi.createEmployee(p),
    onSuccess: () => { toast.success('Employee created'); qc.invalidateQueries({ queryKey: ['employees'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => hrmApi.updateEmployee(id, payload),
    onSuccess: () => { toast.success('Employee updated'); qc.invalidateQueries({ queryKey: ['employees'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => hrmApi.updateEmployee(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'Employee activated' : 'Employee terminated'); qc.invalidateQueries({ queryKey: ['employees'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => hrmApi.deleteEmployee(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Employee deleted'); qc.invalidateQueries({ queryKey: ['employees'] }); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  const { kpis, departments, filteredRows } = useMemo(() => {
    const rows = (data ?? []) as any[];
    const deptSet = new Set<string>();
    let fullTime = 0, partTime = 0, contract = 0, payroll = 0;
    for (const e of rows) {
      if (e.department_name) deptSet.add(e.department_name);
      const ct = String(e.contract_type || '').toLowerCase();
      if (ct.includes('full')) fullTime++;
      else if (ct.includes('part')) partTime++;
      else if (ct.includes('contract')) contract++;
      payroll += parseFloat(e.base_salary) || 0;
    }
    const lower = q.trim().toLowerCase();
    const filtered = rows.filter((e) => {
      if (deptFilter && e.department_name !== deptFilter) return false;
      if (lower) return [e.full_name, e.employee_code, e.designation, e.department_name, e.email].filter(Boolean).join(' ').toLowerCase().includes(lower);
      return true;
    });
    return { kpis: { total: rows.length, active: rows.filter((e) => e.status === 'active').length, payroll, fullTime, partTime, contract }, departments: Array.from(deptSet).sort(), filteredRows: filtered };
  }, [data, q, deptFilter]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="People" title="Employees" subtitle="Your team directory — contacts, compensation, and tenure."
        actions={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New employee</button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Monthly payroll" value={fmtMoney(kpis.payroll)} accent="sage" />
        <KpiTile label="FT / PT / Cx" value={`${kpis.fullTime}/${kpis.partTime}/${kpis.contract}`} />
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by name, code, designation…" value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0" />
        </div>
        {departments.length > 0 && (
          <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1 overflow-x-auto max-w-full">
            <button onClick={() => setDeptFilter('')} className={clsx('px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap', deptFilter === '' ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900')}>All depts</button>
            {departments.map((d) => (
              <button key={d} onClick={() => setDeptFilter(d)} className={clsx('px-3 py-1 text-xs font-semibold rounded-pill transition-all whitespace-nowrap', deptFilter === d ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900')}>{d}</button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? <div className="card"><LoadingState rows={5} /></div>
        : error ? <ErrorState error={error} />
        : !filteredRows.length ? (
          <div className="card">
            <EmptyState message={q || deptFilter ? 'No employees match' : 'No employees yet'}
              hint={q || deptFilter ? 'Try clearing filters.' : 'Add your first employee to get started.'}
              action={!q && !deptFilter && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New employee</button>}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr><th>Employee</th><th>Department</th><th>Designation</th><th>Contract</th><th>Tenure</th><th className="text-right">Salary</th><th>Status</th><th className="text-right w-40">Actions</th></tr>
                </thead>
                <tbody>
                  {filteredRows.map((e: any) => (
                    <tr key={e.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <EmployeeAvatar name={e.full_name} />
                          <div className="min-w-0">
                            <div className="font-semibold text-ink-900 truncate">{e.full_name}</div>
                            <div className="font-mono text-xs text-ink-500">{e.employee_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-ink-700">{e.department_name ?? <span className="text-ink-400">—</span>}</td>
                      <td className="text-sm text-ink-600">{e.designation ?? <span className="text-ink-400">—</span>}</td>
                      <td><span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 capitalize">{(e.contract_type ?? '').replace('_', ' ')}</span></td>
                      <td className="text-sm text-ink-600"><Tenure hireDate={e.hire_date} /></td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtCurrency(parseFloat(e.base_salary))}</td>
                      <td><StatusBadge status={e.status} /></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                            onClick={() => setEditing(e)}>Edit</button>
                          <button
                            className={`text-xs font-medium px-2 py-1 rounded transition-colors ${e.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                            disabled={toggleMut.isPending}
                            onClick={() => toggleMut.mutate({ id: e.id, status: e.status === 'active' ? 'terminated' : 'active' })}>
                            {e.status === 'active' ? 'Terminate' : 'Reinstate'}
                          </button>
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deleteMut.isPending}
                            onClick={() => { if (window.confirm(`Delete employee "${e.full_name}"? If they have linked records they will be marked terminated instead.`)) deleteMut.mutate(e.id); }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <EmployeeModal departments={deptQuery.data ?? []} onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} />}
      {editing && <EmployeeModal departments={deptQuery.data ?? []} initial={editing} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} />}
    </div>
  );
}

function EmployeeAvatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-sage-100 text-sage-700','bg-amber-100 text-amber-800','bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-rose-100 text-rose-700','bg-teal-100 text-teal-700'];
  return <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${tones[hash % tones.length]}`}>{initials}</div>;
}

function Tenure({ hireDate }: { hireDate: string }) {
  if (!hireDate) return <span className="text-ink-400">—</span>;
  const hire = new Date(hireDate);
  if (isNaN(hire.getTime())) return <span className="text-ink-400">—</span>;
  const now = new Date();
  let years = now.getFullYear() - hire.getFullYear();
  let months = now.getMonth() - hire.getMonth();
  if (now.getDate() < hire.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 0) return <span className="text-ink-400">—</span>;
  const str = years > 0 ? `${years}y${months > 0 ? ` ${months}m` : ''}` : months > 0 ? `${months}m` : 'New';
  return (
    <div>
      <div className="font-medium">{str}</div>
      <div className="text-2xs text-ink-400">{hire.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</div>
    </div>
  );
}

function EmployeeModal({ departments, initial, onSubmit, onClose, loading }: {
  departments: any[]; initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const reservationId = useRef<string | null>(null);
  const consumed = useRef(false);
  const [form, setForm] = useState({
    employee_code: initial?.employee_code ?? '',
    full_name: initial?.full_name ?? '',
    designation: initial?.designation ?? '',
    departmentCode: initial?.department_code ?? '',
    contract_type: initial?.contract_type ?? 'full_time',
    hire_date: initial?.hire_date?.slice(0, 10) ?? '',
    base_salary: initial?.base_salary ?? '0',
    pay_frequency: initial?.pay_frequency ?? 'monthly',
  });

  // Hard-reserve an employee code when the create form opens; release it if the
  // form is closed/cancelled without saving (the worker also auto-releases on
  // timeout as a backstop).
  useEffect(() => {
    if (isEdit) return;
    let active = true;
    adminApi.reserveNumber('employee')
      .then((res: any) => {
        if (!active) { adminApi.releaseNumber(res.reservationId).catch(() => {}); return; }
        reservationId.current = res.reservationId;
        setForm((f) => (f.employee_code ? f : { ...f, employee_code: res.number }));
      })
      .catch(() => { /* fall back to manual entry if reservation fails */ });
    return () => {
      active = false;
      if (reservationId.current && !consumed.current) {
        adminApi.releaseNumber(reservationId.current).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · HRM</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit employee' : 'New employee'}</h2>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
        {!isEdit && (
          <div>
            <label className="label">Employee code <span className="text-danger">*</span></label>
            <input className="input font-mono" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="EMP-001" />
          </div>
        )}
        <div className={isEdit ? 'col-span-2' : ''}>
          <label className="label">Full name <span className="text-danger">*</span></label>
          <input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Doe" />
        </div>
        <div>
          <label className="label">Designation</label>
          <input className="input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Store Manager" />
        </div>
        <div>
          <label className="label">Department</label>
          <select className="input" value={form.departmentCode} onChange={(e) => setForm({ ...form, departmentCode: e.target.value })}>
            <option value="">— None —</option>
            {departments.map((d: any) => <option key={d.code} value={d.code}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Contract type</label>
          <select className="input" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })}>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="contractor">Contractor</option>
            <option value="internship">Internship</option>
          </select>
        </div>
        {!isEdit && (
          <div>
            <label className="label">Hire date</label>
            <input type="date" className="input" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
          </div>
        )}
        <div>
          <label className="label">Base salary</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">$</span>
            <input type="number" min={0} step={100} className="input pl-7 tabular-nums" value={form.base_salary}
              onChange={(e) => setForm({ ...form, base_salary: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Pay frequency</label>
          <select className="input" value={form.pay_frequency} onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Bi-weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.full_name || (!isEdit && !form.employee_code)}
          onClick={() => { consumed.current = true; onSubmit(isEdit
            ? { full_name: form.full_name, designation: form.designation || undefined, base_salary: parseFloat(form.base_salary) || 0, pay_frequency: form.pay_frequency }
            : { employee_code: form.employee_code, full_name: form.full_name, designation: form.designation || undefined, departmentCode: form.departmentCode || undefined, contract_type: form.contract_type, hire_date: form.hire_date || undefined, base_salary: parseFloat(form.base_salary) || 0, pay_frequency: form.pay_frequency }); }}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create employee')}
        </button>
      </div>
    </Modal>
  );
}


function KpiTile({ label, value, accent='default' }: { label: string; value: number|string; accent?: 'default'|'sage'|'amber'|'danger' }) {
  const bar = { default:'bg-ink-200', sage:'bg-sage-500', amber:'bg-amber-500', danger:'bg-danger' }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}
const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
